'use client';

import { useEffect, useMemo, useState } from 'react';
import ToggleButton, { ToggleElementType, type ToggleButtonOption } from '@pixum/toggle-button';
import { ACTION_BUTTON_ICON_POSITION, ACTION_BUTTON_STYLE, ActionButton } from '@pixum/action-button';
import { Icon } from '@pixum/icon';
import { Checkbox } from '@pixum/web-checkbox';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import Divider from '@pixum/divider';
import { useCartAddressFormStore } from 'stores';
import { DESTINATION_TYPES } from '../../constants';
import { CartAddressFields } from '../address-form/CartAddressFields';
import { AddressLines, SummaryCard } from '../../../partials';
import type { BillingSectionClientProps, BillingType, CartAddressData, DestinationType } from '../../types';
import styles from '../../CartAddressStep.module.css';
import { BillingAddressDialog } from './partials/BillingAddressDialog';
import { useBillingAddressDialogSave } from './hooks/useBillingAddressDialogSave';
import {
  getBillingCategory,
  getBillingType,
  isBillingType,
  shouldShowBillingAddress,
  shouldShowMissingBillingAddressError
} from './utils';

/**
 * Renders the billing toggle, summary, and optional billing address form.
 * @param props - Initial billing state, device, locale, and localized form constants.
 * @returns The responsive billing address controls and form.
 */
export function BillingSectionClient({
  initialBillingAddress,
  initialBillingMatchesShipping,
  hasSavedDifferentBillingAddress,
  constants,
  locale
}: BillingSectionClientProps) {
  const destinationType: DestinationType = useCartAddressFormStore(state => state.destinationType);
  const billingMatchesShipping = useCartAddressFormStore(state => state.billingMatchesShipping);
  const setBillingMatchesShipping = useCartAddressFormStore(state => state.setBillingMatchesShipping);
  const initializeBillingMatchesShipping = setBillingMatchesShipping;
  const clearBillingFieldErrors = useCartAddressFormStore(state => state.clearBillingFieldErrors);
  const clearShippingFieldErrors = useCartAddressFormStore(state => state.clearShippingFieldErrors);
  const clearBillingFieldError = useCartAddressFormStore(state => state.clearBillingFieldError);
  const billingFieldErrors = useCartAddressFormStore(state => state.billingFieldErrors);

  const [billingType, setBillingType] = useState<BillingType>(getBillingType(initialBillingAddress));
  const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);
  const [billingAddress, setBillingAddress] = useState<CartAddressData | undefined>(initialBillingAddress);
  const billingFormData = useCartAddressFormStore(state => state.formData.billing[billingType]);
  const {
    billingDialogFieldErrors,
    isBillingAddressSaving,
    handleBillingAddressSave,
    handleBillingFieldChange,
    clearBillingDialogFieldErrors
  } = useBillingAddressDialogSave({
    billingAddress,
    billingFormData,
    billingType,
    constants,
    onFieldChange: clearBillingFieldError,
    onSaved: address => {
      setBillingAddress({
        ...address,
        category: getBillingCategory(billingType)
      });
    },
    onClose: () => setIsBillingDialogOpen(false)
  });

  useEffect(() => {
    // Initialize the unchecked state for a saved different billing address without overriding later user changes.
    if (!initialBillingMatchesShipping) {
      initializeBillingMatchesShipping(false);
    }
  }, [initialBillingMatchesShipping, initializeBillingMatchesShipping]);

  const isPickupPoint = destinationType === DESTINATION_TYPES.PICKUP_POINT;
  const hasBillingAddress = Boolean(billingAddress);
  const showMissingBillingAddressError = shouldShowMissingBillingAddressError(
    billingMatchesShipping,
    billingAddress,
    Object.keys(billingFieldErrors).length
  );
  const showBillingAddress = shouldShowBillingAddress(destinationType, billingMatchesShipping);
  // A summary is useful only once there is saved data or the user has entered billing form data.
  const hasBillingSummary = Boolean(hasBillingAddress && (billingFormData || hasSavedDifferentBillingAddress));

  const billingToggleOptions = useMemo(
    () => [
      {
        id: DESTINATION_TYPES.PRIVATE,
        label: constants['i18n_private'],
        icon: <Icon name="home" ariaHidden={true} />
      },
      {
        id: DESTINATION_TYPES.COMPANY,
        label: constants['i18n_company'],
        icon: <Icon name="company" ariaHidden={true} />
      }
    ],
    [constants]
  );
  const selectedBillingOption = billingToggleOptions.find(option => option.id === billingType);

  const handleBillingTypeChange = async (option: ToggleButtonOption) => {
    clearShippingFieldErrors();
    clearBillingFieldErrors();
    clearBillingDialogFieldErrors();

    // ToggleButton accepts generic option ids, so validate the value before writing it to the typed store.
    if (typeof option.id === 'string' && isBillingType(option.id)) {
      setBillingType(option.id);
    }
  };

  const handleBillingDialogOpen = () => {
    clearBillingFieldErrors();
    clearBillingDialogFieldErrors();
    setIsBillingDialogOpen(true);
  };

  const handleBillingDialogClose = () => {
    setBillingType(getBillingType(billingAddress));
    clearBillingDialogFieldErrors();
    setIsBillingDialogOpen(false);
  };

  const handleBillingMatchesShippingChange = (matchesShipping: boolean) => {
    setBillingMatchesShipping(matchesShipping);

    if (matchesShipping) {
      clearBillingFieldErrors();
      clearBillingDialogFieldErrors();
    }
  };

  return (
    <>
      {!isPickupPoint && (
        <div className={styles.cartAddressBillingControl}>
          <div className={styles.cartAddressDesktopOnly}>
            <Checkbox
              checked={billingMatchesShipping}
              id="billingMatchesShippingCheckbox"
              name="billingMatchesShippingCheckbox"
              onChange={setBillingMatchesShipping}
              label={constants['i18n_billing_address_same_as_shipping_address']}
            />
          </div>
          <div className={styles.cartAddressMobileOnly} data-cart-address-mobile-billing>
            <Checkbox
              checked={billingMatchesShipping}
              hasError={showMissingBillingAddressError}
              info={showMissingBillingAddressError ? constants['i18n_billing_address_required'] : undefined}
              id="billingMatchesShippingMobileCheckbox"
              name="billingMatchesShippingMobileCheckbox"
              onChange={handleBillingMatchesShippingChange}
              label={constants['i18n_billing_address_same_as_shipping_address']}
            />
            {!billingMatchesShipping && (
              <>
                <Divider className={`${styles.cartAddressSectionDivider} ${styles.cartAddressMobileBillingDivider}`} />
                <fieldset aria-describedby="billing-address-hint-mobile" className={styles.cartAddressSection}>
                  <legend className={`${styles.cartBillingAddressLegend} text--title3`}>
                    {constants['i18n_different_billing_address']}
                  </legend>
                  <Text id="billing-address-hint-mobile" variant={TEXT_VARIANT.BODY} tag={TEXT_TAG.P}>
                    {constants['i18n_billing_address_warning']}
                  </Text>
                  {hasBillingSummary ? (
                    <SummaryCard
                      actionLabel={constants['i18n_change']}
                      onAction={handleBillingDialogOpen}
                      title={constants['i18n_billing_address']}
                    >
                      {billingAddress && <AddressLines address={billingAddress} locale={locale} />}
                    </SummaryCard>
                  ) : (
                    <ActionButton
                      icon={<Icon name="add-circle" ariaHidden={true} />}
                      iconPosition={ACTION_BUTTON_ICON_POSITION.LEFT}
                      label={constants['i18n_add_different_address']}
                      variant={ACTION_BUTTON_STYLE.FILLED}
                      onClick={() => {
                        setBillingMatchesShipping(false);
                        handleBillingDialogOpen();
                      }}
                    />
                  )}
                </fieldset>
              </>
            )}
          </div>
        </div>
      )}
      {showBillingAddress && (
        <div className={!isPickupPoint ? styles.cartAddressDesktopOnly : undefined}>
          <input name="billingDestinationType" type="hidden" value={billingType} />
          <Divider className={styles.cartAddressSectionDivider} />
          <fieldset
            aria-describedby="billing-address-hint"
            className={`${styles.cartAddressSection} ${styles.cartAddressInlineBilling}`}
          >
            <legend className={`${styles.cartBillingAddressLegend} text--title3`}>
              {constants['i18n_different_billing_address']}
            </legend>
            <Text id="billing-address-hint" variant={TEXT_VARIANT.BODY} tag={TEXT_TAG.P}>
              {constants['i18n_billing_address_warning']}
            </Text>
            <ToggleButton
              groupName="billing-type"
              className={styles.cartAddressToggle}
              toggleElementType={ToggleElementType.INPUT}
              options={billingToggleOptions}
              optionToBeSet={selectedBillingOption}
              onChange={handleBillingTypeChange}
              label={constants['i18n_billing_address']}
            />
            <CartAddressFields
              address={billingAddress}
              locale={locale}
              company={billingType === DESTINATION_TYPES.COMPANY}
              constants={constants}
              fieldNamePrefix="billing_"
              onFieldChange={clearBillingFieldError}
              fieldErrors={billingFieldErrors}
              formScope="billing"
            />
          </fieldset>
        </div>
      )}
      <BillingAddressDialog
        billingAddress={billingAddress}
        billingType={billingType}
        billingToggleOptions={billingToggleOptions}
        constants={constants}
        locale={locale}
        onBillingTypeChange={handleBillingTypeChange}
        onClose={handleBillingDialogClose}
        onFieldChange={handleBillingFieldChange}
        onSave={handleBillingAddressSave}
        fieldErrors={{ ...billingFieldErrors, ...billingDialogFieldErrors }}
        isSaving={isBillingAddressSaving}
        open={isBillingDialogOpen}
        selectedBillingOption={selectedBillingOption}
      />
    </>
  );
}
